import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Lang } from '../i18n';

export type Theme = 'light' | 'dark';
export type Accent = 'indigo' | 'teal' | 'plum' | 'ink';

interface PrefsState {
  theme: Theme;
  lang: Lang;
  accent: Accent;
  setTheme: (t: Theme) => void;
  setLang: (l: Lang) => void;
  setAccent: (a: Accent) => void;
  toggleTheme: () => void;
  toggleLang: () => void;
}

export const ACCENT_COLORS: Record<Accent, { '--accent-blue': string; '--accent-red': string }> = {
  indigo: { '--accent-blue': 'oklch(0.50 0.14 250)', '--accent-red': 'oklch(0.52 0.18 25)' },
  teal: { '--accent-blue': 'oklch(0.55 0.12 200)', '--accent-red': 'oklch(0.58 0.16 30)' },
  plum: { '--accent-blue': 'oklch(0.50 0.12 290)', '--accent-red': 'oklch(0.55 0.16 10)' },
  ink: { '--accent-blue': 'oklch(0.30 0.04 250)', '--accent-red': 'oklch(0.55 0.18 25)' },
};

export const PREFS_VERSION = 1;

const VALID_THEMES: readonly Theme[] = ['light', 'dark'];
const VALID_LANGS: readonly Lang[] = ['zh', 'en'];
const VALID_ACCENTS: readonly Accent[] = ['indigo', 'teal', 'plum', 'ink'];

const DEFAULTS: { theme: Theme; lang: Lang; accent: Accent } = {
  theme: 'light',
  lang: 'zh',
  accent: 'indigo',
};

const pickEnum = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;

export const migratePrefs = (
  state: unknown,
  _fromVersion: number,
): { theme: Theme; lang: Lang; accent: Accent } => {
  if (state == null || typeof state !== 'object') return { ...DEFAULTS };
  const s = state as Record<string, unknown>;
  return {
    theme: pickEnum(s.theme, VALID_THEMES, DEFAULTS.theme),
    lang: pickEnum(s.lang, VALID_LANGS, DEFAULTS.lang),
    accent: pickEnum(s.accent, VALID_ACCENTS, DEFAULTS.accent),
  };
};

export const usePrefs = create<PrefsState>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,
      setTheme: (theme) => set({ theme }),
      setLang: (lang) => set({ lang }),
      setAccent: (accent) => set({ accent }),
      toggleTheme: () => set({ theme: get().theme === 'light' ? 'dark' : 'light' }),
      toggleLang: () => set({ lang: get().lang === 'zh' ? 'en' : 'zh' }),
    }),
    {
      name: 'linalg-prefs',
      version: PREFS_VERSION,
      migrate: (persistedState, version) => {
        const migrated = migratePrefs(persistedState, version);
        return migrated as Partial<PrefsState>;
      },
    },
  ),
);
